import React, { Suspense } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import AOS from 'aos';
import 'aos/dist/aos.css';
import store from '../cart/services/store';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import scrollToElement from '../services/smooth_scroll';
import observeElement from '../services/intersection_observer';
import { campaignsActions } from '../campaigns/actions';
import Product from '../product/components/product/Product';
import Navigation from '../product/components/product/navigation/Navigation';
import Accordion from '../ui_kit/accordion/Accordion';
import IngredientsList from '../product/components/ingredients_list/IngredientsList';
import Benefits from '../product/components/benefits/Benefits';
import HowToUse from '../product/components/how_to_use/HowToUse';
import lightenOrDarker from '../services/lighten_or_darken';

const ProductPairs = React.lazy(() => import('../product/components/product_pairs/ProductPairs'));

window.store = window.store || store;

document.addEventListener('DOMContentLoaded', () => {
  const campaignElement = document.getElementById('react-campaign');
  let { campaign } = campaignElement.dataset;
  campaign = JSON.parse(campaign);
  window.store.dispatch(campaignsActions.setCampaigns([campaign]));

  const searchParams = new URLSearchParams(window.location.search);

  const productElementId = 'react-product';

  const productInfoElement = document.getElementById(productElementId);
  if (productInfoElement) {
    const productData = JSON.parse(document.querySelector(`script[data-dom-id="${productElementId}"]`).textContent);
    const { product, variant, images } = productData;
    hydrateRoot(
      productInfoElement,
      <Provider store={window.store}>
        <Product
          product={product}
          variant={variant}
          images={images}
          campaignId={campaign.id}
        />
      </Provider>,
    );

    const navigationElement = document.getElementById('react-navigation');
    createRoot(
      navigationElement,
    ).render(
      <Navigation
        sections={[
          {
            name: 'Ingredients',
            url: 'ingredients',
          }, {
            name: 'Innovator',
            url: 'innovator',
          }, {
            name: 'Discussion',
            url: 'react-reviews',
          },
        ]}
      />,
    );

    const ingredientsAccordionElementId = 'react-ingredients-accordion-component';
    const ingredientsAccordionElement = document.getElementById(ingredientsAccordionElementId);
    if (ingredientsAccordionElement) {
      const data = JSON.parse(document.querySelector(`script[data-dom-id="${ingredientsAccordionElementId}"]`).textContent);
      const {
        ingredientHeading, ingredientContent, detailedIngredientsHeading, detailedIngredients,
        opened, valueTags,
      } = data;

      hydrateRoot(
        ingredientsAccordionElement,
        <Accordion
          heading={<div dangerouslySetInnerHTML={{ __html: ingredientHeading }} />}
          content={(
            <IngredientsList
              text={ingredientContent}
              detailedIngredientsHeading={detailedIngredientsHeading}
              detailedIngredients={detailedIngredients}
              valueTags={valueTags}
            />
          )}
          background="transparent"
          headingClassName="accordion-head"
          isOpened={opened}
        />,
      );
    }

    const featuresAccordionElementId = 'react-features-accordion-component';
    const featuresAccordionElement = document.getElementById(featuresAccordionElementId);
    if (featuresAccordionElement) {
      const data = JSON.parse(document.querySelector(`script[data-dom-id="${featuresAccordionElementId}"]`).textContent);
      const { featuresHeading, featuresContent, opened } = data;

      hydrateRoot(
        featuresAccordionElement,
        <Accordion
          heading={featuresHeading}
          content={(
            <Benefits
              featuresContent={featuresContent}
              previewText={product.previewText}
              pageColor={campaign.page_color}
            />
            )}
          background="transparent"
          headingClassName="accordion-head"
          isOpened={opened}
        />,
      );
    }

    const howToUseElementId = 'react-howtouse-accordion-component';
    const howToUseElement = document.getElementById(howToUseElementId);
    if (howToUseElement) {
      const data = JSON.parse(document.querySelector(`script[data-dom-id="${howToUseElementId}"]`).textContent);

      const { howToHeading, howToContent, opened } = data;

      hydrateRoot(
        howToUseElement,
        <Accordion
          heading={<div dangerouslySetInnerHTML={{ __html: howToHeading }} />}
          content={<HowToUse text={howToContent} />}
          background="transparent"
          headingClassName="accordion-head"
          isOpened={opened}
        />,
      );
    }

    const additionalProductElement = document.getElementById('react-additional-product');
    if (additionalProductElement) {
      const AdditionalProduct = React.lazy(() => import('../product/components/additional_product/AdditionalProduct'));
      observeElement(additionalProductElement, () => {
        createRoot(
          additionalProductElement,
        ).render(
          <Suspense fallback="">
            <Provider store={window.store}>
              <AdditionalProduct
                product={product}
                variant={variant}
                image={product.background_image}
                pageColor={campaign.page_color}
                campaign={campaign}
              />
            </Provider>
          </Suspense>,
        );
      }, '300px');
    }

    const videoElement = document.getElementById('react-video');
    if (videoElement) {
      const { videoUrl } = videoElement.dataset;

      const Video = React.lazy(() => import('../product/components/video/Video'));

      observeElement(videoElement, () => {
        createRoot(
          videoElement,
        ).render(
          <Suspense fallback="">
            <Video videoUrl={videoUrl} />
          </Suspense>,
        );
      }, '300px');
    }

    const reviewsSection = document.getElementById('react-reviews');
    if (reviewsSection) {
      const ReviewsSection = React.lazy(() => import('../product/components/reviews_section/ReviewsSection'));

      const submitReviewMode = searchParams.get('open_review_form') === 'true';

      createRoot(
        reviewsSection,
      ).render(
        <Suspense fallback="">
          <Provider store={window.store}>
            <ReviewsSection
              sourceId={campaign.id.toString()}
              submitReviewMode={submitReviewMode}
              isCampaign
              customColor={campaign.page_color}
              customTransparentButtonStyles={(
              `\
                .custom-transparent-button{\
                  border: 1px solid ${campaign.page_color};\
                }\
                .custom-transparent-button:hover {\
                  background-color: ${lightenOrDarker(campaign.page_color, -7)}\
                }\
                .loading {\
                  background-color: ${lightenOrDarker(campaign.page_color, -7)} !important\
                }\
              `
            )}
              customFullButtonStyle={(
              `\
                .custom-button{\
                  background-color: ${campaign.page_color};\
                  border: 1px solid ${campaign.page_color};\
                }\
                .custom-button:hover {\
                  background-color: ${lightenOrDarker(campaign.page_color, -7)};\
                  border: 1px solid ${lightenOrDarker(campaign.page_color, -7)};\
                }\
                .loading {\
                  background-color: ${lightenOrDarker(campaign.page_color, -7)} !important\
                }\
              `
            )}
            />
          </Provider>
        </Suspense>,
      );
    }

    const daysLeftSection = document.getElementById('react-days-left');
    if (daysLeftSection) {
      const DaysLeftWrapper = React.lazy(() => import('./components/days_left_wrapper/DaysLeftWrapper'));

      observeElement(daysLeftSection, () => {
        createRoot(
          daysLeftSection,
        ).render(
          <Suspense fallback="">
            <Provider store={window.store}>
              <DaysLeftWrapper product={product} variant={variant} />
            </Provider>
          </Suspense>,
        );
      }, '300px');
    }

    const productFaqElement = document.getElementById('react-productfaq-accordion');
    if (productFaqElement) {
      const { faqHeading, faqContent } = productFaqElement.dataset;

      observeElement(productFaqElement, () => {
        createRoot(
          productFaqElement,
        ).render(
          <Suspense fallback="">
            <Accordion
              heading={<div dangerouslySetInnerHTML={{ __html: faqHeading }} />}
              content={<div dangerouslySetInnerHTML={{ __html: faqContent }} />}
              background="spring-wood"
              headingBackground="spring-wood"
              headingClassName="accordion-head"
              isOpened
            />
          </Suspense>,
        );
      }, '300px');
    }

    const productPairsSection = document.getElementById('product-pairs');
    if (productPairsSection) {
      observeElement(productPairsSection, () => {
        createRoot(
          productPairsSection,
        ).render(
          <Suspense fallback="">
            <Provider store={window.store}>
              <ProductPairs productId={product.id} />
            </Provider>
          </Suspense>,
        );
      }, '300px');
    }
  }

  const questionsAndAnswersSection = document.getElementById('react-qa');
  if (questionsAndAnswersSection) {
    const { firstName } = questionsAndAnswersSection.dataset;
    let { questionsAndAnswers } = questionsAndAnswersSection.dataset;
    questionsAndAnswers = JSON.parse(questionsAndAnswers);

    const QuestionsAndAnswers = React.lazy(() => import('../user/components/user/innovations/questions_and_answers/QuestionsAndAnswers'));

    observeElement(questionsAndAnswersSection, () => {
      createRoot(
        questionsAndAnswersSection,
      ).render(
        <Suspense fallback="">
          <Provider store={window.store}>
            <QuestionsAndAnswers
              firstName={firstName}
              questionsAndAnswers={questionsAndAnswers}
              pageColor={campaign.page_color}
            />
          </Provider>
        </Suspense>,
      );
    }, '300px');
  }

  const scrollToElementId = searchParams.get('scroll_to');
  if (scrollToElementId) {
    scrollToElement(scrollToElementId);
  }

  const StickyCategories = React.lazy(() => import('../homepage/components/sticky_categories/StickyCategories'));
  createRoot(
    document.getElementById('react-sticky-categories'),
  ).render(
    <Suspense fallback="">
      <Provider store={window.store}>
        <StickyCategories />
      </Provider>
    </Suspense>,
  );

  AOS.init();
});
